import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BaseModal from '../../templates/BaseModal';
import ExcelDropzone from '../../templates/ExcelDropzone';

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2.5,
    pt: 2.5,
    pb: 2,
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    bgcolor: theme => theme.bruno.colors.inactive,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerText: { flexGrow: 1 },
  closeButton: { color: 'text.secondary', '&:hover': { color: 'text.primary', bgcolor: 'transparent' } },
  dialogContent: { px: 2.5, py: 2.5 },
  sectionTitle: { mb: 0.5, color: 'text.primary' },
  sectionDescription: { mb: 1.5, lineHeight: 1.5, color: 'text.secondary' },
  divider: { mb: 2.5, borderColor: 'divider' },
  button: {
    borderRadius: 1,
    textTransform: 'none',
    fontWeight: 500,
    py: 1,
    boxShadow: 'none',
    '&:hover': { boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
  },
  downloadButton: { mb: 3 },
  headerIconColor: { fontSize: 20, color: 'text.secondary' },
  buttonIcon: { fontSize: 16 },
  summaryCard: {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1.5,
    p: 2,
    mb: 1,
  },
  summaryRow: { display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 },
  emptySummary: { mt: 1, lineHeight: 1.5, color: 'text.secondary' },
  summaryActions: { display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' },
};

export default function ExcelDialog({
  open,
  onClose,
  currentTaskType,
  downloadExcel,
  previewExcel,
  uploadExcel,
  onUploadSuccess,
}) {
  const { t } = useTranslation();

  // ── State ───────────────────────────
  const [operation, setOperation] = useState(null); // null | 'download' | 'preview' | 'upload'
  const [preview, setPreview] = useState(null); // null | { willCreate, willUpdate, invalid, session, file }

  const downloading = operation === 'download';
  const previewing = operation === 'preview';
  const uploading = operation === 'upload';
  // Nothing to create or update means there is nothing to confirm: the backend
  // rejects a batch with no applicable row, so uploading would only fail.
  const hasChanges = preview ? preview.willCreate + preview.willUpdate > 0 : false;

  const handleClose = () => {
    if (operation !== null) return;
    setPreview(null);
    onClose();
  };

  // ── Actions ─────────────────────────
  const handleDownload = async () => {
    setOperation('download');
    try {
      const result = await downloadExcel(currentTaskType);
      if (result?.success) handleClose();
    } finally {
      setOperation(null);
    }
  };

  // Step 1: dry run — ask the backend how many tasks would be created/updated.
  const handlePreview = async (file) => {
    setOperation('preview');
    try {
      const result = await previewExcel({ task_type: currentTaskType, file });
      if (result?.success) {
        setPreview({
          willCreate: result.willCreate,
          willUpdate: result.willUpdate,
          invalid: result.invalid,
          // Kept so the confirm writes to the session these counts describe.
          session: result.session,
          file,
        });
      }
      return result?.success ?? false;
    } finally {
      setOperation(null);
    }
  };

  // Step 2: run the real upload once the user confirms the summary.
  const handleConfirm = async () => {
    if (!preview || !hasChanges) return;
    setOperation('upload');
    try {
      const result = await uploadExcel({
        task_type: currentTaskType,
        file: preview.file,
        session: preview.session,
      });
      if (result?.success) { onUploadSuccess?.(); handleClose(); }
    } finally {
      setOperation(null);
    }
  };

  const handleCancelPreview = () => {
    if (operation !== null) return;
    setPreview(null);
  };

  // ── Render sections ─────────────────
  const renderHeader = () => (
    <Box sx={styles.header}>
      <Box sx={styles.headerIcon}>
        <ImportExportIcon sx={styles.headerIconColor} />
      </Box>
      <Box sx={styles.headerText}>
        <Typography variant="subtitle1" fontWeight={600} lineHeight={1.3} sx={{ color: 'text.primary' }}>
          {t('bruno.excel.title', 'Excel')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('bruno.excel.subtitle', 'Import or export task data')}
        </Typography>
      </Box>
      <IconButton size="small" onClick={handleClose} sx={styles.closeButton} disabled={operation !== null}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const renderExportSection = () => (
    <>
      <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={styles.sectionTitle}>
        {t('bruno.excel.export_title', 'Export')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={styles.sectionDescription}>
        {t('bruno.excel.export_description', 'Download the current task list as an Excel file.')}
      </Typography>
      <Button
        type="button"
        variant="contained"
        size="small"
        startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <CloudDownloadIcon sx={styles.buttonIcon} />}
        onClick={handleDownload}
        disabled={operation !== null || !currentTaskType}
        fullWidth
        sx={{ ...styles.button, ...styles.downloadButton }}
      >
        {t('bruno.excel.download', 'Download')}
      </Button>
    </>
  );

  const renderPreviewSummary = () => (
    <Box sx={styles.summaryCard}>
      <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
        {t('bruno.excel.preview_title', 'Summary of changes')}
      </Typography>
      <Box sx={styles.summaryRow}>
        <AddCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main' }} />
        <Typography variant="body2" color="text.secondary">
          {t('bruno.excel.preview_create', 'Tasks to create: {{n}}', { n: preview.willCreate })}
        </Typography>
      </Box>
      <Box sx={styles.summaryRow}>
        <EditOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="body2" color="text.secondary">
          {t('bruno.excel.preview_update', 'Tasks to update: {{n}}', { n: preview.willUpdate })}
        </Typography>
      </Box>
      {preview.invalid > 0 && (
        <Box sx={styles.summaryRow}>
          <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />
          <Typography variant="body2" color="text.secondary">
            {t('bruno.excel.preview_invalid', 'Rows to ignore (not in master data): {{n}}', { n: preview.invalid })}
          </Typography>
        </Box>
      )}
      {!hasChanges && (
        <Typography variant="body2" sx={styles.emptySummary}>
          {t(
            'bruno.excel.preview_nothing_to_apply',
            'No row in this file can be applied. Mark the tasks you completed, or add rows for products in the store master data.',
          )}
        </Typography>
      )}
      <Box sx={styles.summaryActions}>
        <Button
          type="button"
          variant="outlined"
          size="small"
          onClick={handleCancelPreview}
          disabled={operation !== null}
          sx={styles.button}
        >
          {t('bruno.excel.cancel', 'Cancel')}
        </Button>
        <Button
          type="button"
          variant="contained"
          size="small"
          startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : null}
          onClick={handleConfirm}
          disabled={operation !== null || !hasChanges}
          sx={styles.button}
        >
          {t('bruno.excel.confirm', 'Confirm')}
        </Button>
      </Box>
    </Box>
  );

  const renderImportSection = () => (
    <>
      <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={styles.sectionTitle}>
        {t('bruno.excel.import_title', 'Import')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={styles.sectionDescription}>
        {t('bruno.excel.import_description', 'Upload an Excel file to create task data or close tasks.')}
      </Typography>
      {preview ? (
        renderPreviewSummary()
      ) : (
        <ExcelDropzone
          uploading={false}
          disabled={operation !== null}
          onUpload={handlePreview}
          actionLabel={
            previewing
              ? t('bruno.excel.loading', 'Loading...')
              : t('bruno.excel.continue', 'Continue')
          }
          warning={t(
            'bruno.excel.upload_warning_tasks',
            'Upload the downloaded file without changing its columns. Mark with 1 the tasks you completed; the rows you leave untouched stay as they are.',
          )}
        />
      )}
    </>
  );

  // ── Layout ──────────────────────────
  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      dialogMaxWidth="xs"
      dialogWidth={400}
      dialogHeight="auto"
      drawerHeight="auto"
      bgcolor="background.paper"
    >
      {renderHeader()}
      <DialogContent sx={styles.dialogContent}>
        {renderExportSection()}
        <Divider sx={styles.divider} />
        {renderImportSection()}
      </DialogContent>
    </BaseModal>
  );
}
